import { Col, DatePicker, Form, Input, Row, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../components/DraggableModal";
import SelectGovernment from "../../../components/selects/SelectGovernment";
import { EntMovableProperty } from "../../../models/movableProperty";
import { SelectRealEstateConditions } from "../../../components/selects";
import dayjs from "dayjs";
import { MapAddSingleMarker } from "../../../components/maps/MapAddPoint";
import { GeoJsonPoint, Geometry } from "../../../models/geoJsonPoint";
import SelectTerritorialUnitsCode from "../../../components/selects/SelectTerritorialUnitsCode";

enum FormField {
  id,
  periodStart,
  periodEnd,
  name,
  brand,
  model,
  quantity,
  description,
  conditionId,
  governmentId,
  regionId,
  areaId,
  territorialUnit,
  address,
}

interface IProps {
  show: boolean;
  movableProperty: EntMovableProperty;
  onHide: () => void;
  onSubmit: (pos: EntMovableProperty) => Promise<boolean>;
}

export const AddMovablePropertyModal = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState(null);
  const [geoJsonData, setGeoJsonData] = useState<GeoJsonPoint|null>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);

  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) setFieldsFromEntity();
	setSelectedRegionId(props.movableProperty.territorialUnitRegion?.id || null);
	setSelectedAreaId(props.movableProperty.territorialUnitArea?.id || null);
	setSelectedAreaCode(props.movableProperty.territorialUnitArea?.code || null);
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  {
		name: FormField.periodStart,
		value: props.movableProperty.periodStart === null ? undefined : dayjs(props.movableProperty.periodStart)
	  },
	  {
		name: FormField.periodEnd,
		value: props.movableProperty.periodEnd === null ? undefined : dayjs(props.movableProperty.periodEnd)
	  },
	  { name: FormField.name, value: props.movableProperty.name },
	  { name: FormField.brand, value: props.movableProperty.brand },
	  { name: FormField.model, value: props.movableProperty.model },
	  { name: FormField.quantity, value: props.movableProperty.quantity },
	  { name: FormField.description, value: props.movableProperty.description },
	  { name: FormField.conditionId, value: props.movableProperty.condition?.id },
	  { name: FormField.governmentId, value: props.movableProperty.government?.id },
	  { name: FormField.regionId, value: props.movableProperty.territorialUnitRegion?.id },
	  { name: FormField.areaId, value: props.movableProperty.territorialUnitArea?.id },
	  { name: FormField.territorialUnit, value: props.movableProperty.territorialUnit?.id },
	  { name: FormField.address, value: props.movableProperty.address },
	]);
  };

  const updateGeoJsonPoint = () => {
	const latitude = props.movableProperty.latitude;
	const longitude = props.movableProperty.longitude;

	if (latitude !== undefined && longitude !== undefined) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t('breeding_farms.error_geometry'));
	}
  };

  useEffect(() => {
	if (props.movableProperty.latitude && props.movableProperty.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.movableProperty.latitude, props.movableProperty.longitude]);

  const setEntityFromFields = (): EntMovableProperty => {
	let res = _.cloneDeep(props.movableProperty);
	res.periodStart = form.getFieldValue(FormField.periodStart)?.format("YYYY-MM-DD");
	res.periodEnd = form.getFieldValue(FormField.periodEnd)?.format("YYYY-MM-DD");
	res.name = form.getFieldValue(FormField.name);
	res.brand = form.getFieldValue(FormField.brand);
	res.model = form.getFieldValue(FormField.model);
	res.quantity = Number(form.getFieldValue(FormField.quantity));
	res.description = form.getFieldValue(FormField.description);
	res.conditionId = form.getFieldValue(FormField.conditionId);
	res.governmentId = form.getFieldValue(FormField.governmentId);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnit);
	res.address = form.getFieldValue(FormField.address);
	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	return res;
  }
  const hideModal = () => {
	props.onHide();
	setSelectedRegionId(null);
	setSelectedRegionPolygonJson(null);
	setSelectedAreaCode(null);
	setGeoJsonData(null);
  }
  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  const data = setEntityFromFields();
	  const result = await props.onSubmit(data);
	  if (result) hideModal();
	} finally {
	  setLoading(false);
	}
  }


  const handleStartDateChange = (date: any) => {
	setStartDate(date);
  };

  const disableEndDate = (current: any) => {
	return current && current.isBefore(startDate, 'day');
  };
  return (
	<>
	  <DraggableModal
		title={ props.movableProperty.id === 0 ? t('general.add') : t('general.edit') }
		visible={ props.show }
		onCancel={ () => {
		  hideModal()
		} }
		onSubmit={ handleSubmit }
		submitLoading={ loading }
		width={ 1000 }
	  >
		<Form
		  initialValues={ { remember: true } }
		  autoComplete="off"
		  layout="vertical"
		  form={ form }
		>
		  <Row style={ { margin: 0 } } gutter={ 16 }>
			<Col className="gutter-row" span={ 12 }>
			  <div className="block_item_form">
				<Form.Item
				  label={ t('titles.governments') }
				  name={ FormField.governmentId }
				  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				>
				  <SelectGovernment/>
				</Form.Item>
			  </div>
			  <Form.Item
				label={ t('general.name') }
				name={ FormField.name }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="text"/>
			  </Form.Item>
			  <Form.Item
				label={ t('movable_property.brand') }
				name={ FormField.brand }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="text"/>
			  </Form.Item>
			  <Form.Item
				label={ t('movable_property.model') }
				name={ FormField.model }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="text"/>
			  </Form.Item>
			  <Form.Item
				label={ t('breeding_farms.quantity') }
				name={ FormField.quantity }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="number"/>
			  </Form.Item>
			  <Form.Item
				label={ t('mechanisation.total_account.address') }
				name={ FormField.address }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="text"/>
			  </Form.Item>
			</Col>
			<Col className="gutter-row" span={ 12 }>
			  <div className="block_item_form">
				<Space.Compact>
				  <Form.Item
					label={ t('general.start_date_period') }
					name={ FormField.periodStart }
					rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

				  >
					<DatePicker onChange={ handleStartDateChange } placeholder={ t('general.select_date') }/>
				  </Form.Item>
				  <Form.Item
					label={ t('general.end_date_period') }
					name={ FormField.periodEnd }
					rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }

				  >
					<DatePicker disabledDate={ disableEndDate } placeholder={ t('general.select_date') }/>
				  </Form.Item>
				</Space.Compact>
			  </div>
			  <Form.Item
				label={ t('titles.transport_status') }
				name={ FormField.conditionId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectRealEstateConditions/>
			  </Form.Item>

			  <Form.Item
				label={ t('titles.territorial_unit_area') }
				name={ FormField.regionId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode
				  parentId={ 1 }
				  onChange={ (value, option: any) => {
					setSelectedRegionId(Number(value));
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedRegionCode(option?.data?.code);
					setSelectedAreaCode(null);
					setSelectedAreaId(null);
					form.setFieldsValue({
					  [FormField.regionId]: value,
					  [FormField.areaId]: null, // Сбрасываем район
					  [FormField.territorialUnit]: null, // Сбрасываем населенный пункт
					});
				  } }
				/>
			  </Form.Item>
				<>
			  { selectedRegionId || props.movableProperty.territorialUnitRegion ? (
				<Form.Item
				  label={ t('titles.territorialUnitDistricts') }
				  name={ FormField.areaId }
				  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				>
				  <SelectTerritorialUnitsCode
					type={ ['AREA'] }
					parentId={ selectedRegionId }
					onChange={ (value, option: any) => {
					  setSelectedRegionPolygonJson(option?.data?.polygonJson);
					  setSelectedAreaCode(option?.data?.code || null);
					  setSelectedAreaId(value as number);
					  form.setFields([{ name: FormField.territorialUnit, value: null }]);
					} }
				  />
				</Form.Item>
			  ) : null }

			  { selectedAreaCode || props.movableProperty.territorialUnit ? (
				<Form.Item
				  label={ t("titles.territorialUnitVillage") }
				  name={ FormField.territorialUnit }
				  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
				>
				  <SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
				</Form.Item>
			  ) : null }
				</>
			  <Form.Item
				label={ t('mechanisation.transport.description') }
				name={ FormField.description }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<TextArea rows={ 6 } maxLength={ 1000 } showCount/>
			  </Form.Item>
			</Col>
		  </Row>
		  <MapAddSingleMarker
			polygonJson={ selectedRegionPolygonJson }
			setGeoJsonData={ setGeoJsonData }
			geoJsonData={ geoJsonData }
		  />
		</Form>
	  </DraggableModal>
	</>
  )
}
